import { useCallback, useEffect, useState } from 'react';
import {
  differenceInCalendarDays,
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { useBusiness } from '../contexts/BusinessContext';
import { usePermissionCheck } from './usePermissionCheck';
import { reportMoney, runReport } from '../lib/reports';
import {
  getCurrentPeriodRange,
  getPayrollPeriodSettings,
  type PayrollPeriodSettings,
} from '../lib/payrollPeriodSettings';

function formatPeriodLabel(start: Date, end: Date): string {
  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameYear && start.getMonth() === end.getMonth()) {
    return `${format(start, 'MMM d')} – ${format(end, 'd, yyyy')}`;
  }
  if (sameYear) {
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
  }
  return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
}

function getPeriodProgress(start: Date, end: Date, now: Date): number {
  const periodStart = startOfDay(start);
  const periodEnd = startOfDay(end);
  const today = startOfDay(now);
  const totalDays = Math.max(1, differenceInCalendarDays(periodEnd, periodStart) + 1);
  if (today < periodStart) return 0;
  if (today > periodEnd) return 1;
  const elapsedDays = differenceInCalendarDays(today, periodStart) + 1;
  return Math.min(1, elapsedDays / totalDays);
}

export function useControlCentreBannerMetrics() {
  const { selectedBusiness } = useBusiness();
  const { canAccess } = usePermissionCheck();
  const businessId = selectedBusiness?.id;

  const [payrollTotal, setPayrollTotal] = useState(0);
  const [periodLabel, setPeriodLabel] = useState('');
  const [periodProgress, setPeriodProgress] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadPayroll = useCallback(async () => {
    if (!businessId || !canAccess.payroll) {
      setPayrollTotal(0);
      setPeriodLabel('');
      setPeriodProgress(0);
      setLoading(false);
      return;
    }

    try {
      let settings: PayrollPeriodSettings | null = null;
      try {
        settings = await getPayrollPeriodSettings(businessId);
      } catch {
        settings = null;
      }

      const now = new Date();
      const range = settings
        ? getCurrentPeriodRange(settings)
        : { start: startOfMonth(now), end: endOfMonth(now) };

      const startStr = format(range.start, 'yyyy-MM-dd');
      const endStr = format(range.end, 'yyyy-MM-dd');

      // What this period will pay out: closed work, shifts still running, and tips.
      const result = await runReport(
        businessId,
        `FROM profit SHOW payroll, live_payroll, tips SINCE ${startStr} UNTIL ${endStr}`,
      );
      const total =
        reportMoney(result, 'payroll') + reportMoney(result, 'live_payroll') + reportMoney(result, 'tips');

      setPayrollTotal(total);
      setPeriodLabel(formatPeriodLabel(range.start, range.end));
      setPeriodProgress(getPeriodProgress(range.start, range.end, now));
    } catch (err) {
      console.error('Error loading control centre payroll metric:', err);
      setPayrollTotal(0);
    } finally {
      setLoading(false);
    }
  }, [businessId, canAccess.payroll]);

  useEffect(() => {
    setLoading(true);
    loadPayroll();
    const interval = setInterval(loadPayroll, 60_000);
    return () => clearInterval(interval);
  }, [loadPayroll]);

  return {
    payrollTotal,
    periodLabel,
    periodProgress,
    loading,
    // Hide the payroll metric entirely when there's nothing to pay this period
    // (also keeps it hidden during the initial load, where the total is 0).
    showPayroll: canAccess.payroll && payrollTotal > 0,
    refreshPayroll: loadPayroll,
  };
}
